import { useState } from 'react';
import { Button, message, Modal, Tooltip } from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import { useT } from '../../../locales';
import { useResetUserPassword } from '../api/useUsers';
import { TemporaryPasswordModal } from './TemporaryPasswordModal';

export interface ResetUserPasswordActionProps {
  userId: string;
  iconOnly?: boolean;
  hiddenTrigger?: boolean;
  /** Full-width button for stacked mobile action rows. */
  block?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ResetUserPasswordAction({
  userId,
  iconOnly = false,
  hiddenTrigger = false,
  block = false,
  open,
  onOpenChange,
}: ResetUserPasswordActionProps) {
  const t = useT();
  const resetPassword = useResetUserPassword();
  const [internalConfirmationOpen, setInternalConfirmationOpen] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const confirmationOpen = open ?? internalConfirmationOpen;

  const setConfirmationOpen = (nextOpen: boolean) => {
    if (open === undefined) {
      setInternalConfirmationOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  const handleConfirm = async () => {
    try {
      const result = await resetPassword.mutateAsync(userId);
      setConfirmationOpen(false);
      setTempPassword(result.tempPassword);
      message.success(t('pages.users.resetPasswordSuccess'));
    } catch {
      message.error(t('pages.users.resetPasswordError'));
    }
  };

  const handleAcknowledge = () => {
    setTempPassword(null);
    resetPassword.reset();
  };

  const resetButton = (
    <Button
      type={iconOnly ? 'text' : 'default'}
      icon={<KeyOutlined />}
      aria-label={t('pages.users.resetPassword')}
      loading={resetPassword.isPending}
      block={block}
      onClick={() => setConfirmationOpen(true)}
    >
      {!iconOnly && t('pages.users.resetPassword')}
    </Button>
  );

  return (
    <>
      {!hiddenTrigger &&
        (iconOnly ? (
          <Tooltip title={t('pages.users.resetPassword')}>{resetButton}</Tooltip>
        ) : (
          resetButton
        ))}
      <Modal
        open={confirmationOpen}
        title={t('pages.users.resetPasswordTitle')}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
        confirmLoading={resetPassword.isPending}
        onOk={handleConfirm}
        onCancel={() => setConfirmationOpen(false)}
        destroyOnHidden
      >
        <p>{t('pages.users.resetPasswordConfirm')}</p>
      </Modal>
      <TemporaryPasswordModal
        open={tempPassword !== null}
        tempPassword={tempPassword}
        onAcknowledge={handleAcknowledge}
      />
    </>
  );
}
